import { verifyEmail } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Link } from 'react-router';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(t('auth.invalidToken'));
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Redirect to inbox after short delay
        setTimeout(() => {
          navigate(routes.inbox);
        }, 2000);
      } catch (err) {
        setStatus('error');
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(t('auth.invalidTokenDesc'));
        }
      }
    };

    verify();
  }, [token, navigate, t]);

  if (status === 'loading') {
    return (
      <div className="space-y-6 text-center">
        <Loader2 className="w-12 h-12 text-blue mx-auto animate-spin" />
        <h2 className="text-2xl font-medium text-ink">{t('auth.verifying')}</h2>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="space-y-6 text-center">
        <CheckCircle className="w-12 h-12 text-sage mx-auto" />
        <h2 className="text-2xl font-medium text-ink">{t('auth.emailVerified')}</h2>
        <p className="text-sepia">{t('auth.emailVerifiedDesc')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-center">
      <XCircle className="w-12 h-12 text-red mx-auto" />
      <h2 className="text-2xl font-medium text-ink">{t('auth.invalidToken')}</h2>
      <p className="text-sepia">{error}</p>
      <p className="text-sm text-sepia/70">
        <Link to={routes.login} className="text-blue hover:underline font-medium">
          {t('common.back')}
        </Link>
      </p>
    </div>
  );
}
